import type { EnvironmentId, ThreadId } from "@cmmd-forge/contracts";
import { memo, useEffect, useEffectEvent, useRef, useState } from "react";

import { readEnvironmentApi } from "~/environmentApi";
import { newCommandId } from "~/lib/utils";
import { Popover } from "../ui/popover";
import { toastManager } from "../ui/toast";
import {
  EditableGoalTrigger,
  GOAL_MAX_LENGTH,
  GoalEditorPopup,
  ProjectLeadGoalBadge,
  TaskManagedGoalBadge,
} from "./ThreadGoalChipRenderers";
import { PROJECT_LEAD_THREAD_GOAL } from "~/lib/projectLeadTaskCreationThread";

const CHAR_WARNING_THRESHOLD = 450;

export interface ThreadGoalChipProps {
  readonly goal: string | null | undefined;
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly taskManaged?: boolean;
  readonly onUpdateGoal?: (goal: string | null) => Promise<void>;
  readonly editor?:
    | {
        readonly open: boolean;
        readonly anchor: HTMLElement | null;
        readonly onOpenChange: (open: boolean) => void;
      }
    | undefined;
}

export function isThreadGoalEditable(props: Pick<ThreadGoalChipProps, "goal" | "taskManaged">) {
  return props.goal !== PROJECT_LEAD_THREAD_GOAL && !props.taskManaged;
}

export function threadGoalMenuLabel(props: Pick<ThreadGoalChipProps, "goal" | "taskManaged">) {
  if (props.goal === PROJECT_LEAD_THREAD_GOAL) return "Project Lead conversation";
  if (props.taskManaged)
    return props.goal ? `Task-managed goal: ${props.goal}` : "Task-managed goal";
  return props.goal ? "Edit thread goal" : "Set thread goal";
}

/**
 * Thread goal control. Task-managed goals are read-only. Standalone thread
 * goals retain the editable popover.
 */
export const ThreadGoalChip = memo(function ThreadGoalChip(props: ThreadGoalChipProps) {
  return props.goal === PROJECT_LEAD_THREAD_GOAL ? (
    <ProjectLeadGoalBadge />
  ) : props.taskManaged ? (
    <TaskManagedGoalBadge goal={props.goal} />
  ) : (
    <EditableThreadGoalChip
      goal={props.goal}
      environmentId={props.environmentId}
      threadId={props.threadId}
      {...(props.editor ? { editor: props.editor } : {})}
      {...(props.onUpdateGoal ? { onUpdateGoal: props.onUpdateGoal } : {})}
    />
  );
});

function EditableThreadGoalChip(props: Omit<ThreadGoalChipProps, "taskManaged">) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const popoverOpen = props.editor?.open ?? uncontrolledOpen;
  const initializeEditor = () => {
    setInputValue(props.goal ?? "");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
        textareaRef.current?.select();
      });
    });
  };
  const handleOpenChange = (open: boolean) => {
    if (open) {
      initializeEditor();
    }
    if (props.editor) {
      props.editor.onOpenChange(open);
      return;
    }
    setUncontrolledOpen(open);
  };

  const updateGoal = async (goal: string | null, failureTitle: string) => {
    const api = readEnvironmentApi(props.environmentId);
    if (!api && !props.onUpdateGoal) {
      handleOpenChange(false);
      return;
    }

    setSaving(true);
    try {
      if (props.onUpdateGoal) {
        await props.onUpdateGoal(goal);
      } else {
        await api!.orchestration.dispatchCommand({
          type: "thread.meta.update",
          commandId: newCommandId(),
          threadId: props.threadId,
          goal,
        });
      }
    } catch (error) {
      toastManager.add({
        type: "error",
        title: failureTitle,
        description: error instanceof Error ? error.message : "An error occurred.",
      });
      handleOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCommit = () => {
    const trimmed = inputValue.trim();
    const nextGoal = trimmed.length > 0 ? trimmed : null;
    if (nextGoal === (props.goal ?? null)) {
      handleOpenChange(false);
      return;
    }
    void updateGoal(nextGoal, "Failed to update goal");
  };

  const handleClear = () => {
    if (props.goal == null) {
      handleOpenChange(false);
      return;
    }
    void updateGoal(null, "Failed to clear goal");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleCommit();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      handleOpenChange(false);
    }
  };

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || !popoverOpen) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 80)}px`;
  }, [inputValue, popoverOpen]);

  const initializeControlledEditor = useEffectEvent(initializeEditor);
  useEffect(() => {
    if (props.editor?.open) initializeControlledEditor();
  }, [props.editor?.open]);

  const hasGoal = props.goal != null && props.goal.length > 0;
  const nearLimit = inputValue.length >= CHAR_WARNING_THRESHOLD;
  const atLimit = inputValue.length >= GOAL_MAX_LENGTH;
  const trimmedUnchanged = inputValue.trim() === (props.goal ?? "");

  return (
    <Popover open={popoverOpen} onOpenChange={handleOpenChange}>
      {props.editor ? null : <EditableGoalTrigger goal={props.goal} />}
      <GoalEditorPopup
        textareaRef={textareaRef}
        inputValue={inputValue}
        hasGoal={hasGoal}
        nearLimit={nearLimit}
        atLimit={atLimit}
        saving={saving}
        trimmedUnchanged={trimmedUnchanged}
        onInputChange={(value) => {
          if (value.length <= GOAL_MAX_LENGTH) setInputValue(value);
        }}
        onKeyDown={handleKeyDown}
        onCommit={handleCommit}
        onClear={handleClear}
        {...(props.editor?.anchor ? { anchor: props.editor.anchor } : {})}
      />
    </Popover>
  );
}
